import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
// core components
import Header from '../components/Headers/Header'
import {
  getRestaurantDetail,
  deleteFood,
  updateFoodOutOfStock
} from '../apollo'
import FoodComponent from '../components/Food/Food'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import { transformToNewline } from '../utils/stringManipulations'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import {
  Box,
  Container,
  Modal,
  FormControl,
  Switch,
  FormControlLabel,
  Button
} from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import TableHeader from '../components/TableHeader'
import Alert from '../components/Alert'
import ConfigurableValues from '../config/constants'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_FOODS = gql`
  ${getRestaurantDetail}
`
const DELETE_FOOD = gql`
  ${deleteFood}
`
const UPDATE_FOOD_OUT_OF_STOCK = gql`
  ${updateFoodOutOfStock}
`

const handleStockChange = async (foodId, checked) => {
  if (!PAID_VERSION) {
    setIsOpen(true)
    setTimeout(() => {
      setIsOpen(false)
    }, 5000)
    return
  }

  try {
    await updateStockStatus({
      variables: {
        id: foodId,
        outofstock: checked
      }
    })
  } catch (error) {
    console.error('Error updating stock status:', error)
  }
}

const Food = props => {
  const { t } = props
  const { PAID_VERSION } = ConfigurableValues()
  const [editModal, setEditModal] = useState(false)
  const [food, setFood] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [addModal, setAddModal] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const onChangeSearch = e => setSearchQuery(e.target.value)
  const restaurantId = localStorage.getItem('restaurantId')

  // Add mutation hook for updating stock status
  const [updateStockStatus] = useMutation(UPDATE_FOOD_OUT_OF_STOCK, {
    refetchQueries: [{ query: GET_FOODS, variables: { id: restaurantId } }]
  })

  const [mutate, { loading }] = useMutation(DELETE_FOOD, {
    refetchQueries: [{ query: GET_FOODS, variables: { id: restaurantId } }]
  })
  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_FOODS,
    {
      variables: {
        id: restaurantId
      }
    }
  )
  const toggleModal = food => {
    setEditModal(!editModal)
    setFood(food)
  }
  const closeEditModal = () => {
    setEditModal(false)
  }

  const propExists = (obj, path) => {
    return path.split('.').reduce((obj, prop) => {
      return obj && obj[prop] ? obj[prop] : ''
    }, obj)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (field && isNaN(propExists(row, field))) {
        return propExists(row, field).toLowerCase()
      }

      return row[field]
    }
    return orderBy(rows, handleField, direction)
  }

  // Handle stock status change
  const handleStockChange = async (foodId, categoryId, checked) => {
    if (!PAID_VERSION) {
      setIsOpen(true)
      setTimeout(() => {
        setIsOpen(false)
      }, 5000)
      return
    }

    try {
      await updateStockStatus({
        variables: {
          id: foodId,
          restaurant: restaurantId,
          categoryId: categoryId
        }
      })
    } catch (error) {
      console.error('Error updating stock status:', error)
    }
  }

  const columns = [
    {
      name: t('Title'),
      selector: 'title',
      sortable: true,
      cell: row => <>{row.emoji ? `${row.title} ${row.emoji}` : row.title}</>
    },
    {
      name: t('Description'),
      sortable: true,
      selector: 'description',
      cell: row => <>{transformToNewline(row.description, 3)}</>
    },
    {
      name: t('Category'),
      sortable: true,
      selector: 'category.category',
      cell: row => <>{row.category}</>
    },
    {
      name: t('Out of Stock'),
      sortable: true,
      selector: 'isOutOfStock',
      cell: row => (
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={row.isOutOfStock}
                onChange={e =>
                  handleStockChange(row._id, row.categoryId, e.target.checked)
                }
                color="primary"
              />
            }
          />
        </FormControl>
      )
    },
    {
      name: t('Image'),
      cell: row => (
        <>
          <img
            className="img-responsive"
            style={{ width: 30, height: 30, borderRadius: 15 }}
            src={
              row.image ||
              'https://res.cloudinary.com/dxrcjcgfw/image/upload/v1741326240/white_h7ykg5.png'
            }
            alt={row.image ? 'img menu' : 'Default Image'}
          />
        </>
      )
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]
  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  if (PAID_VERSION) toggleModal(row)
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 5000)
                  }
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                divider: true,
                confirm: {
                  title: `${t('Delete')}?`,
                  message: t('DeleteConfirm'),
                  cancelLabel: t('Cancel')
                },
                onClick: () => {
                  if (PAID_VERSION)
                    mutate({
                      variables: {
                        id: row._id,
                        restaurant: restaurantId,
                        categoryId: row.categoryId
                      }
                    })
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 5000)
                  }
                }
              }
            ]}
          />
        </div>
      </>
    )
  }

  const foodsList = categories => {
    const list = []
    categories &&
      categories.forEach(category => {
        // `foods` is pulled out so a row does not carry its whole sibling list.
        const { foods = [], ...categoryFields } = category
        foods.forEach(item => {
          list.push({
            // Category first, food second, so the food's own fields always win.
            // Spreading the category last overwrote `image` with the category
            // picture: every row showed its category's photo, and because this
            // row is what the edit modal receives as `food`, a replaced product
            // image looked like it had reverted the next time it was opened.
            ...categoryFields,
            ...item,
            category: category.title,
            categoryId: category._id,
            emoji: item.emoji || ''
          })
        })
      })
    return list
  }
  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null

  const filtered =
    searchQuery.length < 3
      ? foodsList(data && data.restaurant.categories)
      : foodsList(data && data.restaurant.categories).filter(food => {
          return (
            food.title.toLowerCase().search(regex) > -1 ||
            food.description.toLowerCase().search(regex) > -1 ||
            food.category.toLowerCase().search(regex) > -1
          )
        })
  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {t('AddFood')}
          </Button>
        }
      />
      {/* Page content */}
      {isOpen && (
        <Alert message={t('AvailableAfterPurchasing')} severity="warning" />
      )}
      <Container className={globalClasses.flex} fluid>
        {errorQuery && <span>`Error! ${errorQuery.message}`</span>}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('Food')} />}
            columns={columns}
            data={data && data.restaurant ? filtered : {}}
            pagination
            progressPending={loading}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="title"
            customStyles={customStyles}
            selectableRows
            paginationIconLastPage=""
            paginationIconFirstPage=""
          />
        )}
        <Modal open={editModal} onClose={closeEditModal} sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            {/* Keyed on the food being edited so React reuses this instance
                across unrelated re-renders of this page. Without it a background
                refetch (the stock toggle and every save trigger one) could
                re-create the form mid-edit, and its state — including the image
                the user just picked — reset to the saved values. */}
            <FoodComponent
              key={food?._id}
              food={food}
              onClose={closeEditModal}
            />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <FoodComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Food)
